import { type FC, type ReactNode, type RefObject, useCallback } from 'react';

import type { VirtualElement } from '@floating-ui/react';
import classNames from 'classnames';
import type { CSSProperties } from 'styled-components';

import { NavbarMenuItemDropdownV2 } from '../navbarMenuItemElement/components/NavbarMenuItemDropdown';

import { getBackgroundColorStyle } from '../../utils/background';

import type { NavbarMenuDropdownElementProps } from '../../navbarElement/Navbar.type';
import * as styles from '../Navbar.css';
import type { NavbarMenuItemV2ElementProps } from '../navbarMenuItemElement';
import {
  NAVBAR_MENU_DROPDOWN_CARD_DEFAULTS_V2,
  NAVBAR_MENU_DROPDOWN_FULL_DEFAULTS_V2,
  NAVBAR_MENU_DROPDOWN_MOBILE_DEFAULTS_V2,
} from './NavbarMenuDropdown.constants';

export function NavbarMenuDropdownV2(
  props: NavbarMenuDropdownElementProps & {
    controlbyselected: boolean | string;
    screen: 'desktop' | 'mobile';
    navbarItemElementProps?: NavbarMenuItemV2ElementProps & {
      controlbyselected: boolean | string;
      selected: boolean;
      virtualElementRef?: RefObject<VirtualElement | undefined>;
    };
    shouldRenderWithNavbarMenuItemDropdown?: boolean;
    isInsideBurgerMenu?: boolean;
  },
) {
  const {
    element,
    screen,
    className,
    children,
    controlbyselected: controlbyselectedProp,
    attributes,
    navbarItemElementProps,
    shouldRenderWithNavbarMenuItemDropdown,
    isInEditor = false,
    isInsideBurgerMenu,
  } = props;
  let controlbyselected = controlbyselectedProp;
  if (typeof controlbyselected === 'string') {
    controlbyselected = controlbyselected === 'true';
  }
  const {
    style,
    mobileStyle,
    padding,
    background,
    border_radius,
    gap,
    customAttributes,
    // Mobile attributes
    mobileBackground,
    mobilePadding,
    mobileGap,
  } = element.attrs || {};

  const useMobileStyle = !!mobileStyle && screen === 'mobile';

  let DEFAULTS = style === 'card' ? NAVBAR_MENU_DROPDOWN_CARD_DEFAULTS_V2 : NAVBAR_MENU_DROPDOWN_FULL_DEFAULTS_V2;
  if (useMobileStyle) {
    DEFAULTS = mobileStyle === 'card' ? NAVBAR_MENU_DROPDOWN_CARD_DEFAULTS_V2 : NAVBAR_MENU_DROPDOWN_FULL_DEFAULTS_V2;
  }

  const Wrapper: FC<{ children: ReactNode }> = useCallback(
    ({ children }) => {
      return shouldRenderWithNavbarMenuItemDropdown && navbarItemElementProps ? (
        <NavbarMenuItemDropdownV2 {...navbarItemElementProps}>
          {children}
          {/* Separator is inside the NavbarMenuItemDropdown component */}
        </NavbarMenuItemDropdownV2>
      ) : (
        <>{children}</>
      );
    },
    [navbarItemElementProps, shouldRenderWithNavbarMenuItemDropdown],
  );

  if (screen === 'mobile' && !element.attrs?.parentInMobile && (!isInEditor || isInsideBurgerMenu)) {
    const shouldOpen = [attributes?.['data-is-open'], navbarItemElementProps?.attributes?.['data-is-open']].includes(
      'true',
    );

    return (
      // biome-ignore lint/a11y/useValidAriaRole: <explanation>
      <div
        {...props}
        role="none"
        {...customAttributes}
        className={classNames(
          styles.navbarMobileDropdownV2,
          className,
          customAttributes?.className,
          !useMobileStyle && styles.navbarMobileDropdownFullWidthV2,
        )}
        style={
          {
            '--padding': mobilePadding || (padding ?? NAVBAR_MENU_DROPDOWN_MOBILE_DEFAULTS_V2.padding),
            '--border-radius': border_radius ?? DEFAULTS.borderRadius,
            '--gap': mobileGap || (gap ?? NAVBAR_MENU_DROPDOWN_MOBILE_DEFAULTS_V2.gap),
            '--width': DEFAULTS.width,
            ...getBackgroundColorStyle(
              mobileBackground || (background ?? NAVBAR_MENU_DROPDOWN_MOBILE_DEFAULTS_V2.background),
            ),
            display: shouldRenderWithNavbarMenuItemDropdown && !shouldOpen ? 'none' : undefined,
          } as CSSProperties
        }
      >
        {children}
      </div>
    );
  }

  return (
    <Wrapper>
      {/* biome-ignore lint/a11y/useValidAriaRole: <explanation> */}
      <div
        {...props}
        {...attributes}
        role="none"
        {...customAttributes}
        className={classNames(styles.navbarDropdownV2, className, customAttributes?.className)}
        style={
          {
            '--padding': padding ?? DEFAULTS.padding,
            '--border-radius': border_radius ?? DEFAULTS.borderRadius,
            '--width': DEFAULTS.width,
            '--gap': gap ?? DEFAULTS.gap,
            ...getBackgroundColorStyle(background ?? DEFAULTS.background),
          } as CSSProperties
        }
      >
        {children}
      </div>
    </Wrapper>
  );
}
